/**
 * P1-ENGINE D6 — three-stage shape solver: block candidate loop → validity →
 * height targeting with re-validation.
 *
 * Binding rules (P1 card deliverable 6 + geometry-contract.md §2-§3):
 * - **Block arcs stay in calc** (single source of truth): this module uses the
 *   verified arc formula height = R − cos(lengthToContact/R)·R with the
 *   block's upturnRadius — the same formula profileCurve.ts documents.
 * - **Geometric vs production validity** (P0-GEOM split): geometric = closed
 *   shape math (positive lengths, contact points inside the ski); production
 *   = effective-edge and height sanity against block reality.
 * - **Never force a result** (evaluation-metrics fallback honesty): when no
 *   block reaches the target height, report the RESIDUAL and the best
 *   candidates — the designer decides. No silent clamping.
 * - **Determinism**: pure functions; candidates ranked by (residual, block code).
 * - All block data from the verified prod fixture
 *   (docs/reference/fixtures/ullr-reference-blocks-rockers-20260925.json).
 */
import blocksJson from '../../docs/reference/fixtures/ullr-reference-blocks-rockers-20260925.json';

export const SOLVER_VERSION = '0.1';

/** One NoseTailBlock from the verified prod reference fixture. */
export interface SolverBlock {
  id: number;
  code: string;
  upturnRadiusMm: number;
  lengthOfBlockMm: number;
}

interface BlocksFixture {
  noseTailBlock: Array<Record<string, unknown>>;
  _source?: string;
}

const fix = blocksJson as unknown as BlocksFixture;

/** All 8 production blocks, deduped by code (ids 1 & 2 share code '15'). */
export const PRODUCTION_BLOCKS: SolverBlock[] = Object.values(
  fix.noseTailBlock.reduce<Record<string, SolverBlock>>((acc, b) => {
    const code = String(b.code);
    if (!acc[code]) {
      acc[code] = {
        id: Number(b.id),
        code,
        upturnRadiusMm: Number(b.upturnRadius),
        lengthOfBlockMm: Number(b.lengthOfBlock),
      };
    }
    return acc;
  }, {}),
);

/**
 * Verified block-arc formula (calc/src/orderformcalc.ts, documented in
 * profileCurve.ts): height at the ski's end for a given contact length.
 */
export function arcHeightMm(upturnRadiusMm: number, lengthToContactMm: number): number {
  return upturnRadiusMm - Math.cos(lengthToContactMm / upturnRadiusMm) * upturnRadiusMm;
}

/** Invert the arc formula: what contact length reaches `targetHeightMm` on R? */
export function contactLengthForHeight(upturnRadiusMm: number, targetHeightMm: number): number | null {
  if (targetHeightMm <= 0 || targetHeightMm >= upturnRadiusMm) return null;
  return Math.acos(1 - targetHeightMm / upturnRadiusMm) * upturnRadiusMm;
}

// ---------------------------------------------------------------------------
// Stage 1: block candidate loop
// ---------------------------------------------------------------------------

export interface Stage1Candidate {
  block: SolverBlock;
  /** Contact length the target height implies on this block's radius. */
  impliedContactLengthMm: number;
  /** Height this block would produce at the ski's allowed contact length. */
  achievableHeightMm: number;
  /** |achievable − target| — 0 is perfect. */
  residualMm: number;
}

export interface Stage1Result {
  candidates: Stage1Candidate[];
  /** Candidates sorted by residual asc, then block code asc (deterministic). */
  ranked: Stage1Candidate[];
}

/**
 * Stage 1: for every production block, compute the contact length the target
 * height implies and whether that fits inside the ski's end region
 * (maxContactMm). Height beyond the radius is impossible (cos saturates) —
 * those blocks report their achievable height at max contact, with the
 * residual stating the shortfall.
 */
export function stage1BlockCandidates(input: {
  /** Which end: nose or tail (block pool is shared; kept explicit for audit). */
  end: 'nose' | 'tail';
  targetHeightMm: number;
  /** Max contact length the ski's geometry allows for this end (mm). */
  maxContactLengthMm: number;
  blocks?: SolverBlock[];
}): Stage1Result {
  const blocks = input.blocks ?? PRODUCTION_BLOCKS;
  const candidates: Stage1Candidate[] = [];
  for (const block of blocks) {
    const implied = contactLengthForHeight(block.upturnRadiusMm, input.targetHeightMm);
    const contact = implied !== null && implied <= input.maxContactLengthMm
      ? implied
      : input.maxContactLengthMm;
    const achievable = arcHeightMm(block.upturnRadiusMm, contact);
    candidates.push({
      block,
      impliedContactLengthMm: implied ?? contact,
      achievableHeightMm: achievable,
      residualMm: Math.abs(achievable - input.targetHeightMm),
    });
  }
  const ranked = [...candidates].sort((a, b) => {
    const d = a.residualMm - b.residualMm;
    if (d !== 0) return d;
    return a.block.code.localeCompare(b.block.code, undefined, { numeric: true });
  });
  return { candidates, ranked };
}

// ---------------------------------------------------------------------------
// Stage 2: validity (geometric vs production — the P0-GEOM split)
// ---------------------------------------------------------------------------

export interface ValidityCheck {
  geometric: 'valid' | 'warning' | 'invalid';
  production: 'valid' | 'warning' | 'invalid' | 'unchecked';
  /** Every message names the field/quantity at fault (geometry-contract §5). */
  notes: string[];
}

export function checkDesignValidity(input: {
  lengthMm: number;
  noseContactLengthMm: number;
  tailContactLengthMm: number;
  noseHeightMm: number;
  tailHeightMm: number;
  widthsMm: { nose: number; waist: number; tail: number };
  /** Recommended max total end-consumption of the ski (production rule). */
  maxEndConsumptionRatio?: number;
}): ValidityCheck {
  const notes: string[] = [];
  let geometric: ValidityCheck['geometric'] = 'valid';
  let production: ValidityCheck['production'] = 'valid';

  // Geometric: closed-shape math
  const endConsumption = input.noseContactLengthMm + input.tailContactLengthMm;
  if (input.lengthMm <= 0) {
    geometric = 'invalid';
    notes.push(`lengthMm: ${input.lengthMm} is non-positive — closed shape impossible`);
  } else if (endConsumption >= input.lengthMm) {
    geometric = 'invalid';
    notes.push(
      `nose contact ${input.noseContactLengthMm} + tail contact ${input.tailContactLengthMm} = ${endConsumption} mm consumes the full ${input.lengthMm} mm length — no effective edge remains`,
    );
  } else if (endConsumption > input.lengthMm * (input.maxEndConsumptionRatio ?? 0.5)) {
    geometric = 'warning';
    notes.push(
      `end contact total ${endConsumption} mm exceeds ${(input.maxEndConsumptionRatio ?? 0.5) * 100}% of length (${input.lengthMm} mm) — running length only ${input.lengthMm - endConsumption} mm`,
    );
  }
  for (const [k, v] of Object.entries(input.widthsMm)) {
    if (!(v > 0)) {
      geometric = 'invalid';
      notes.push(`width ${k}: ${v} mm is non-positive — closed shape impossible`);
    }
  }
  for (const [k, v] of [['nose', input.noseHeightMm], ['tail', input.tailHeightMm]] as const) {
    if (!(v >= 0)) {
      geometric = 'invalid';
      notes.push(`${k} height: ${v} mm is negative — arc math broken`);
    }
  }

  // Production: block/height sanity beyond pure geometry
  const minSaneHeight = 2; // mm — below this the "upturn" is press noise
  for (const [k, v] of [['nose', input.noseHeightMm], ['tail', input.tailHeightMm]] as const) {
    if (v >= 0 && v < minSaneHeight) {
      production = 'warning';
      notes.push(`${k} height ${v.toFixed(2)} mm below ${minSaneHeight} mm sanity floor — check block selection`);
    }
  }

  if (notes.length === 0) {
    notes.push('all checks passed');
  }
  return { geometric, production, notes };
}

// ---------------------------------------------------------------------------
// Stage 3: height targeting with re-validation
// ---------------------------------------------------------------------------

export interface HeightTargetResult {
  /** Best block found (lowest residual). Null only when the block pool is empty. */
  best: Stage1Candidate | null;
  /** Final achieved height at the chosen contact length. */
  achievedHeightMm: number;
  /** |achieved − target| — reported, never silently clamped away. */
  residualMm: number;
  /** Whether the target was reached within tolerance. */
  reached: boolean;
  /** Post-selection re-validation (stage 2 over the chosen geometry). */
  validity: ValidityCheck;
  /** Full ranked candidates — transparency for the designer. */
  ranked: Stage1Candidate[];
  notes: string[];
}

export interface HeightTargetingInput {
  end: 'nose' | 'tail';
  targetHeightMm: number;
  lengthMm: number;
  /** Contact length budget for this end (mm) — from the ski's dims. */
  maxContactLengthMm: number;
  toleranceMm?: number;
  /** The other end's contact length (for end-consumption validity). */
  otherEndContactLengthMm: number;
  otherEndHeightMm: number;
  widthsMm: { nose: number; waist: number; tail: number };
  blocks?: SolverBlock[];
}

/**
 * Stage 3: pick the best block for the target height, then RE-VALIDATE the
 * resulting whole-ski geometry. Reports residual + reached — never forces.
 */
export function solveHeightTarget(input: HeightTargetingInput): HeightTargetResult {
  const tolerance = input.toleranceMm ?? 2;
  const { ranked } = stage1BlockCandidates({
    end: input.end,
    targetHeightMm: input.targetHeightMm,
    maxContactLengthMm: input.maxContactLengthMm,
    blocks: input.blocks,
  });
  const notes: string[] = [];
  const best = ranked[0] ?? null;
  if (!best) {
    return {
      best: null,
      achievedHeightMm: 0,
      residualMm: Math.abs(input.targetHeightMm),
      reached: false,
      validity: { geometric: 'invalid', production: 'unchecked', notes: ['no production blocks available'] },
      ranked,
      notes: ['block pool empty'],
    };
  }
  if (best.residualMm > tolerance) {
    notes.push(
      `${input.end}: no block reaches ${input.targetHeightMm} mm within ±${tolerance} mm — best ${best.block.code} achieves ${best.achievableHeightMm.toFixed(2)} mm (residual ${best.residualMm.toFixed(2)} mm). Designer decides: accept the residual or change the target.`,
    );
  }

  // Re-validate the whole-ski geometry with the chosen end's numbers.
  const thisEndContact = Math.min(best.impliedContactLengthMm, input.maxContactLengthMm);
  const otherContact = input.otherEndContactLengthMm;
  const noseContact = input.end === 'nose' ? thisEndContact : otherContact;
  const tailContact = input.end === 'tail' ? thisEndContact : otherContact;
  const noseHeight = input.end === 'nose' ? best.achievableHeightMm : input.otherEndHeightMm;
  const tailHeight = input.end === 'tail' ? best.achievableHeightMm : input.otherEndHeightMm;
  const validity = checkDesignValidity({
    lengthMm: input.lengthMm,
    noseContactLengthMm: noseContact,
    tailContactLengthMm: tailContact,
    noseHeightMm: noseHeight,
    tailHeightMm: tailHeight,
    widthsMm: input.widthsMm,
  });

  return {
    best,
    achievedHeightMm: best.achievableHeightMm,
    residualMm: best.residualMm,
    reached: best.residualMm <= tolerance,
    validity,
    ranked,
    notes,
  };
}

// ---------------------------------------------------------------------------
// Block lookup (D7 integration: precedents carry noseBlockId/tailBlockId)
// ---------------------------------------------------------------------------

/** All 8 production block rows (ids 1-8; ids 1 & 2 share code '15'). */
export const ALL_BLOCKS: SolverBlock[] = fix.noseTailBlock.map((b) => ({
  id: Number(b.id),
  code: String(b.code),
  upturnRadiusMm: Number(b.upturnRadius),
  lengthOfBlockMm: Number(b.lengthOfBlock),
}));

/** Look up a block by its production id (OrderForm.noseBlockId/tailBlockId). */
export function blockById(id: number): SolverBlock | null {
  return ALL_BLOCKS.find((b) => b.id === id) ?? null;
}
